import {
  type DragEvent,
  type ReactNode,
  type SyntheticEvent,
  useRef,
  useState,
} from 'react'
import cx from 'classnames'
import type { Observable } from 'rxjs'
import { RawButton } from '@/components/Button'
import { Dropdown, DropdownItem } from '@/components/Dropdown'
import { Icon } from '@/components/Icon'
import { IconButton } from '@/components/IconButton'
import { Pill } from '@/components/Pill'
import { Skeleton, useSkeletonListContext } from '@/components/Skeleton'
import { Tooltip } from '@/components/Tooltip'
import { useObservable } from '@/hooks/rx-hooks'
import { pluralize } from '@/utils/pluralization-util'
import { TypeBadge } from '@/features/legends/components/TypeBadge'
import type { RynoType } from '@/features/legends/editor/ryno-types'
import type { DropPosition } from '@/features/legends/questions/question-drag'
import type { QuestionType } from '@/features/legends/questions/question-types'
import { QuestionIcon } from '@/features/legends/questions/components/QuestionIcon'
import { QuestionPreviewSkeleton } from '@/features/legends/questions/components/QuestionPreview'
import styles from './QuestionCard.module.css'

export interface QuestionCardProps {
  type: QuestionType
  id: string
  /**
   * Shown next to the ID, like a meta element's label.
   */
  title?: string
  /**
   * The type's name, for untitled elements.
   */
  friendlyType: string
  error?: string
  /**
   * Marks an element of a deprecated type, which can't be edited.
   */
  isDeprecated?: boolean
  conditionCount: number
  valueType?: RynoType | null
  isSelected?: boolean
  isCollapsible?: boolean
  isCollapsed?: boolean
  isDisabled?: boolean
  onPress: () => unknown
  onToggleCollapse: () => unknown
  /**
   * Double-clicking the header focuses the inspector's ID field.
   */
  onFocusId: () => unknown
  /**
   * Shows the menu item that duplicates the element.
   */
  onDuplicate?: () => unknown
  onRemove: () => unknown
  /**
   * Shows the menu item that adds the legend's service items.
   */
  onSetupInventoryItems?: () => unknown
  onDragStart: () => unknown
  onDragEnd: () => unknown
  /**
   * Whether what is being dragged over the card can be dropped next to it.
   */
  canAcceptDrop: () => boolean
  onDrop: (position: DropPosition) => unknown
  /**
   * Alt and the up or down arrow on the header move the card one place.
   */
  onMove: (delta: -1 | 1) => unknown
  scrollIntoView$?: Observable<unknown>
  /**
   * What the client sees, under the header. Meta elements have none.
   */
  preview?: ReactNode
  /**
   * A path's questions, under the card while it is expanded.
   */
  pathQuestions?: ReactNode
  testId?: string
}

/**
 * A question on the canvas: its ID, title and a preview, draggable to
 * another place.
 */
export function QuestionCard({
  type,
  id,
  title,
  friendlyType,
  error,
  isDeprecated,
  conditionCount,
  valueType,
  isSelected,
  isCollapsible,
  isCollapsed,
  isDisabled,
  onPress,
  onToggleCollapse,
  onFocusId,
  onDuplicate,
  onRemove,
  onSetupInventoryItems,
  onDragStart,
  onDragEnd,
  canAcceptDrop,
  onDrop,
  onMove,
  scrollIntoView$,
  preview,
  pathQuestions,
  testId,
}: QuestionCardProps) {
  const cardRef = useRef<HTMLDivElement>(null)
  const [dropPosition, setDropPosition] = useState<DropPosition | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  useObservable(scrollIntoView$, () =>
    cardRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' }),
  )

  const press = (e: SyntheticEvent) => {
    e.stopPropagation()
    onPress()
  }

  const card = (
    // The header's button selects the card from the keyboard; pressing
    // anywhere else on the card selects it too.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      ref={cardRef}
      className={cx(
        styles.card,
        isSelected && styles.selected,
        !!error && styles.invalid,
        isDragging && styles.dragging,
      )}
      draggable={!isDisabled}
      onDragStart={(e) => {
        // Cards of a path's questions sit inside the path's group; the
        // innermost card is the one dragged.
        e.stopPropagation()
        e.dataTransfer.effectAllowed = 'move'
        // Firefox only starts a drag with data.
        e.dataTransfer.setData('text/plain', '')
        setIsDragging(true)
        onDragStart()
      }}
      onDragEnd={() => {
        setIsDragging(false)
        onDragEnd()
      }}
      onDragOver={(e) => {
        if (!canAcceptDrop()) {
          return
        }
        e.preventDefault()
        e.stopPropagation()
        setDropPosition(getDropPosition(e))
      }}
      onDragLeave={(e) => !isWithin(e) && setDropPosition(null)}
      onDrop={(e) => {
        e.preventDefault()
        e.stopPropagation()
        setDropPosition(null)
        onDrop(getDropPosition(e))
      }}
      onClick={press}
      data-test={testId}
    >
      {dropPosition && (
        <span
          className={cx(
            styles.dropLine,
            dropPosition === 'above' ? styles.above : styles.below,
          )}
        />
      )}
      <div className={styles.header}>
        {isCollapsible && (
          <IconButton
            name="chevron-down"
            label={isCollapsed ? 'Expand' : 'Collapse'}
            size={14}
            rotate={isCollapsed ? -90 : 0}
            aria-expanded={!isCollapsed}
            onPress={(e) => {
              e.stopPropagation()
              onToggleCollapse()
            }}
            testId={testId && `${testId}-collapse-button`}
          />
        )}
        <RawButton
          className={styles.info}
          aria-current={isSelected || undefined}
          onPress={press}
          onDoubleClick={onFocusId}
          onKeyDown={(e) => {
            if (isDisabled) {
              return
            }
            if (e.key === 'Delete') {
              e.preventDefault()
              onRemove()
            } else if (
              e.altKey &&
              (e.key === 'ArrowUp' || e.key === 'ArrowDown')
            ) {
              e.preventDefault()
              const button = e.currentTarget
              onMove(e.key === 'ArrowUp' ? -1 : 1)
              // React moves the card's element to its new place, which can
              // drop the focus.
              requestAnimationFrame(() => button.focus())
            }
          }}
          testId={testId && `${testId}-select-button`}
        >
          {error ? (
            <Tooltip content={error}>
              <span className={styles.errorIcon}>
                <Icon name="status-alert" size={20} aria-hidden="true" />
              </span>
            </Tooltip>
          ) : (
            <QuestionIcon type={type} size={20} />
          )}
          <span className={styles.headerText}>
            <span className={styles.titleRow}>
              {id && <span className={styles.id}>{id}</span>}
              {title ? (
                <span className={cx(styles.title, !id && styles.titleOnly)}>
                  {title}
                </span>
              ) : (
                !id && (
                  <span className={styles.untitled}>
                    Untitled {friendlyType}
                  </span>
                )
              )}
              {isDeprecated && (
                <Pill size="small" testId={testId && `${testId}-deprecated`}>
                  Deprecated
                </Pill>
              )}
            </span>
            {(error || conditionCount > 0) && (
              <span
                className={cx(styles.subtitle, !!error && styles.subtitleError)}
              >
                {error ??
                  `${conditionCount} ${pluralize('condition', conditionCount)}`}
              </span>
            )}
          </span>
          <TypeBadge type={valueType} />
        </RawButton>
        {!isDisabled && (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- keeps menu presses from selecting the card
          <div className={styles.actions} onClick={(e) => e.stopPropagation()}>
            <Dropdown
              autoDismiss
              position="bottom-end"
              testId={testId && `${testId}-menu`}
              button={
                <IconButton
                  name="more"
                  label="Question actions"
                  size={16}
                  testId={testId && `${testId}-menu-button`}
                />
              }
            >
              {onSetupInventoryItems && (
                <DropdownItem
                  icon={<Icon name="settings-outline" />}
                  onPress={onSetupInventoryItems}
                  testId={testId && `${testId}-set-up-inventory-items`}
                >
                  Set up inventory items
                </DropdownItem>
              )}
              {onDuplicate && (
                <DropdownItem
                  icon={<Icon name="copy" />}
                  onPress={onDuplicate}
                  testId={testId && `${testId}-duplicate`}
                >
                  Duplicate
                </DropdownItem>
              )}
              <DropdownItem
                icon={<Icon name="delete" />}
                onPress={onRemove}
                testId={testId && `${testId}-remove`}
              >
                Remove
              </DropdownItem>
            </Dropdown>
          </div>
        )}
      </div>
      {preview && <div className={styles.body}>{preview}</div>}
    </div>
  )

  if (pathQuestions === undefined) {
    return card
  }

  return (
    <div className={styles.group}>
      {card}
      {!isCollapsed && (
        <div
          className={cx(styles.children, isSelected && styles.childrenSelected)}
        >
          {pathQuestions}
        </div>
      )}
    </div>
  )
}

/**
 * A skeleton for a {@link QuestionCard}.
 */
export function QuestionCardSkeleton() {
  const { vary } = useSkeletonListContext()
  return (
    <div className={cx(styles.card, styles.skeleton)}>
      <div className={styles.header}>
        <span className={styles.info}>
          <Skeleton variant="rect" width={20} height={20} />
          <span className={styles.headerText}>
            <span className={styles.titleRow}>
              <Skeleton width={vary(110)} />
            </span>
          </span>
          <Skeleton variant="rect" width={vary(48)} height={20} />
        </span>
      </div>
      <div className={styles.body}>
        <QuestionPreviewSkeleton />
      </div>
    </div>
  )
}

/**
 * Above or below the card's middle.
 */
function getDropPosition(e: DragEvent<HTMLElement>): DropPosition {
  const rect = e.currentTarget.getBoundingClientRect()
  return e.clientY < rect.top + rect.height / 2 ? 'above' : 'below'
}

/**
 * Dragging over a child fires `dragleave` on the parent, which isn't leaving.
 */
function isWithin(e: DragEvent<HTMLElement>) {
  return e.currentTarget.contains(e.relatedTarget as Node | null)
}
